import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { Box, Button, Flex, HStack, Stack } from "@chakra-ui/react";
import { formatDate } from "utils";
import { Trash } from "@phosphor-icons/react";
import { LabelText, ValueText } from "components/text/TextTypes";
import { getFileType, getFolderForRound, KB_FOLDER } from "api/common/fileinfo";
import fileinfoService from "api/common/fileinfo.service";
import {
  apiAddToFolders,
  apiArchiveFileInfo,
  apiRemoveFromFolders,
  apiRestoreFromArchiveFileInfo,
} from "api/common/fileinfo.service";
import { apiGetAISource, deleteAISourceByFileInfoId, postSourcesUpdate } from "api/ai/ai.service";
import { queryKey } from "api/api-utils";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import SlowButton from "components/SlowButton";
import { useState, useEffect } from "react";
import AIProgressWidget from "../AITasks/AIProgressWidget";
import DevOnly from "context/Auth/DevOnly";
import useAirUserRoles from "../../../utils/AuthUtils";
import PropInput from "components/PropInput";
import FileDownloadButton from "components/FileDownloadButton";

/**
 * Figma: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1067-55922&t=gwunprNWRkbVoArB-0
 * @param {Object} p
 * @param {FileInfo} p.item
 */
export default function SourceDetailsRSidebar({ item, folder }) {
  const queryClient = useQueryClient();

  const { isController } = useAirUserRoles();

  const [inRound, setInRound] = useState();
  useEffect(() => {
    setInRound(item.folders?.includes(folder));
  }, [item, item.folders, folder]);

  const toggleInRound = () => {
    const p = inRound ? apiRemoveFromFolders(item, folder) : apiAddToFolders(item, folder);
    setInRound(!inRound); // update the UI immediately
    return p.then(() => {
      queryClient.invalidateQueries("fileinfoService.list");
    });
  };

  const toggleArchive = () => {
    let p;
    if (item.archived) {
      p = apiRestoreFromArchiveFileInfo(item);
      p.then(() => {
        postSourcesUpdate([item.id], true);
      });
    } else {
      p = apiArchiveFileInfo(item);
      // p.then(() => { Not needed, as the AI will check for files
      // 	deleteAISourceByFileInfoId(item.id);
      // });
    }
    return p.then(() => {
      queryClient.invalidateQueries("fileinfoService.list");
      queryClient.invalidateQueries(["fileinfoService.list", "archived"]);
    });
  };

  const { data: aiSource } = useQuery(queryKey("apiGetSource", item.id), () =>
    apiGetAISource(item).then((data) => {
      // copy periodStart and periodEnd to item for convenience
      if (data?.periodStart) item.periodStart = data.periodStart;
      if (data?.periodEnd) item.periodEnd = data.periodEnd;
      return data;
    })
  );
  const handleDeleteAI = () => {
    return deleteAISourceByFileInfoId(item.id).then(() => {
      queryClient.invalidateQueries(queryKey("apiGetSource", item.id));
    });
  };

  const [aiAnalysisTask, setAiAnalysisTask] = useState();
  const handleSendForAnalysis = () => {
    return postSourcesUpdate([item.id], false).then((data) => {
      setAiAnalysisTask(data);
      queryClient.invalidateQueries(queryKey("apiGetSource", item.id));
    });
  };

  const handleEdit = (prop, value) => {
    console.log("handleEdit", item, prop, value);
    fileinfoService.update(item.id, item).then(() => {
      queryClient.invalidateQueries(queryKey("apiGetSource", item.id));
    });
  };

  return (
    <Box id="SourceDetailsPanel" h="full" display="flex" flexDirection="column">
      <RightSidebarHeader>{item.title || item.filename}</RightSidebarHeader>
      <Flex direction="column" justify="space-between" flexGrow={1}>
        <Box>
          {item.title && item.title !== item.filename && (
            <>
              <LabelText>File Name</LabelText>
              <ValueText>{item.filename}</ValueText>
            </>
          )}

          <LabelText>Upload Date</LabelText>
          <ValueText>{formatDate(item.createdDate)}</ValueText>

          <LabelText>Type</LabelText>
          <Box display='flex' flexDirection='row' justifyContent='left'><ValueText>{getFileType(item)}</ValueText><FileDownloadButton height='inherit' width='auto' fileInfo={item} /></Box>
          <LabelText>AI Analysis</LabelText>
          <ValueText>{formatDate(item.aiDate)}</ValueText>

          {aiSource?.regions?.length && (
            <>
              <LabelText>Regions</LabelText>
              <ValueText>{aiSource.regions.join(", ")}</ValueText>
            </>
          )}

          <>
            <LabelText>Period</LabelText>
            <Flex direction="row">
              <PropInput
                inline
                label="Start"
                prop="periodStart"
                item={item}
                type="date"
                onChange={(e) => handleEdit("periodStart", e.target.value)}
                max={item?.periodEnd}
              />
              <PropInput
                ml={4}
                inline
                label="End"
                prop="periodEnd"
                item={item}
                type="date"
                onChange={(e) => handleEdit("periodEnd", e.target.value)}
                min={item?.periodStart}
              />
            </Flex>
          </>

          {aiSource?.summary && (
            <>
              <LabelText>Summary</LabelText>
              <ValueText>{aiSource.summary}</ValueText>
            </>
          )}

          <DevOnly>
            <LabelText>File ID</LabelText>
            <ValueText>{item.id}</ValueText>
            <LabelText>Folder</LabelText>
            <ValueText>{item.folders?.join(", ")}</ValueText>
          </DevOnly>

          {aiSource?.error && (
            <>
              <LabelText color="red">Analysis Issue</LabelText>
              <ValueText>{aiSource.error}</ValueText>
            </>
          )}
        </Box>
        <Flex gap={2} direction="column">
          <HStack>
            <DevOnly>
              <SlowButton w="full" variant="secondary" isDisabled={!!aiAnalysisTask} onClick={handleSendForAnalysis}>
                Send for AI Analysis
              </SlowButton>
            </DevOnly>
            {aiAnalysisTask && <AIProgressWidget taskId={aiAnalysisTask?.id} />}
          </HStack>
          {isController() && (
            <SlowButton w="full" variant={inRound ? "secondary" : "primary"} onClick={toggleInRound}>
              {inRound ? "Remove from" : "Add to"} Round
            </SlowButton>
          )}
          {!item.archived && isController() && (
            <SlowButton w="full" variant="secondary" onClick={toggleArchive}>
              Archive <Trash style={{ marginLeft: "0.5em" }} color="red" size={20} />
            </SlowButton>
          )}
          {item.archived && isController() && (
            <SlowButton w="full" variant="secondary" onClick={toggleArchive}>
              Restore from Archive
            </SlowButton>
          )}
          {/* <DevOnly>
            <SlowButton w="full" variant="secondary" onClick={handleDeleteAI}>
              Remove from AI Memory
            </SlowButton>
          </DevOnly> */}
        </Flex>
      </Flex>
    </Box>
  );
}
